/**
 * Helpers for deriving org-context display values from the backend-served
 * {@link Identity} (`GET /api/tenant-management/me`).
 *
 * These replace the old claim-parsing helpers: org identity, the active org id,
 * and the org name now come from the tenant-management backend, not from a token
 * decoded in the browser. The admin/staff booleans are computed server-side and
 * read directly off the {@link Identity} (no helper needed).
 *
 * @module utils/identity
 */

import type { Identity } from '../types/identity';

/**
 * Fallback organization name when identity is absent or the active org carries
 * no name.
 */
export const DEFAULT_ORG_NAME = 'Tenant Management';

/**
 * Returns the id of the caller's active (home) org, or `null` when it can't be
 * resolved.
 *
 * This is the org id a **normal admin** materializes into the mandatory
 * `{orgId}` URL segment for member operations (a staff admin instead targets the
 * org they pick). Fail-closed: `null` for a missing identity or a blank id —
 * callers MUST guard on `null` and skip the request rather than build a
 * malformed `/orgs//users` URL.
 *
 * @param identity - identity from the backend, or null when not authenticated
 * @returns the active org id, or null if it can't be resolved
 */
export function activeOrgIdFromIdentity(
  identity: Identity | null | undefined,
): string | null {
  const id = identity?.activeOrg?.id;
  return typeof id === 'string' && id.trim() ? id : null;
}

/**
 * Returns the display name of the caller's active (home) org, falling back to a
 * generic default when identity is absent or the org has no name.
 *
 * @param identity - identity from the backend, or null when not authenticated
 * @returns the active org's name, or a default fallback value
 */
export function orgNameFromIdentity(
  identity: Identity | null | undefined,
): string {
  const name = identity?.activeOrg?.name;
  return typeof name === 'string' && name.trim() ? name : DEFAULT_ORG_NAME;
}
